import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack, Save as SaveIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import recetaService from '../../services/recetaService';
import productoService, { OPTIONS_PRODUCTO_OPERATIVO } from '../../services/productoService';
import { formatCurrency } from '../../utils/formatters';
import RecetaProductoLookup from './RecetaProductoLookup';
import RecetaComponentesGrid, { RecetaComponenteLinea } from './RecetaComponentesGrid';
import { mapProductoToRecetaArticulo } from './RecetaArticuloSearchDialog';

const newLinea = (): RecetaComponenteLinea => ({
  id: `c-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  codigoArticulo: '',
  descripcion: '',
  unidadMedida: '',
  cantidad: 1,
  costo: 0,
});

const RecetaForm: React.FC = () => {
  const { codigoReceta } = useParams();
  const isNew = !codigoReceta || codigoReceta === 'nuevo';
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [codigo, setCodigo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [ptCodigo, setPtCodigo] = useState('');
  const [ptDescripcion, setPtDescripcion] = useState('');
  const [ptCantidad, setPtCantidad] = useState(1);
  const [ptCostoActual, setPtCostoActual] = useState(0);
  const [lineas, setLineas] = useState<RecetaComponenteLinea[]>([newLinea()]);
  const [saving, setSaving] = useState(false);
  const [updatingCosto, setUpdatingCosto] = useState(false);
  const [confirmCostoOpen, setConfirmCostoOpen] = useState(false);
  const [pendingCostoUnitario, setPendingCostoUnitario] = useState(0);

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await recetaService.getReceta(decodeURIComponent(codigoReceta!));
        if (cancelled) return;
        setCodigo(data.codigoReceta);
        setDescripcion(data.descripcion);
        const ptCode = data.productoTerminado?.codigoArticulo ?? '';
        setPtCodigo(ptCode);
        setPtDescripcion(data.productoTerminado?.descripcion ?? '');
        setPtCantidad(data.productoTerminado?.cantidad ?? 1);

        if (ptCode) {
          try {
            const p = await productoService.getProducto(ptCode, OPTIONS_PRODUCTO_OPERATIVO);
            if (!cancelled) setPtCostoActual(Number(p.costo ?? 0) || 0);
          } catch {
            if (!cancelled) setPtCostoActual(0);
          }
        }

        const componentes = data.componentes?.length ? data.componentes : [];
        if (componentes.length === 0) {
          setLineas([newLinea()]);
          return;
        }

        const enriched = await Promise.all(
          componentes.map(async (c) => {
            const base: RecetaComponenteLinea = {
              id: `c-${c.codigoArticulo}-${Math.random().toString(36).slice(2, 8)}`,
              codigoArticulo: c.codigoArticulo,
              descripcion: c.descripcion ?? '',
              unidadMedida: '',
              cantidad: c.cantidad,
              costo: 0,
            };
            try {
              const p = await productoService.getProducto(c.codigoArticulo, OPTIONS_PRODUCTO_OPERATIVO);
              const mapped = mapProductoToRecetaArticulo(p);
              return {
                ...base,
                descripcion: mapped.descripcion || base.descripcion,
                unidadMedida: mapped.unidadMedida,
                costo: mapped.costo,
              };
            } catch {
              return base;
            }
          }),
        );
        if (!cancelled) setLineas(enriched);
      } catch {
        enqueueSnackbar('No se pudo cargar la receta', { variant: 'error' });
        navigate('/produccion/recetas');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [codigoReceta, isNew, enqueueSnackbar, navigate]);

  const costoReceta = useMemo(
    () =>
      lineas
        .filter((l) => l.codigoArticulo.trim())
        .reduce((acc, l) => acc + (Number(l.cantidad) || 0) * (Number(l.costo) || 0), 0),
    [lineas],
  );

  const costoUnitarioPt = useMemo(() => {
    const qty = Number(ptCantidad) || 0;
    if (qty <= 0) return 0;
    return costoReceta / qty;
  }, [costoReceta, ptCantidad]);

  const finishAndNavigate = () => {
    setConfirmCostoOpen(false);
    navigate('/produccion/recetas');
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const componentes = lineas
        .filter((c) => c.codigoArticulo.trim())
        .map((c) => ({
          codigoArticulo: c.codigoArticulo.trim(),
          descripcion: (c.descripcion ?? '').trim(),
          cantidad: Number(c.cantidad) || 0,
        }));

      const payload = {
        descripcion: descripcion.trim(),
        productoTerminado: {
          codigoArticulo: ptCodigo.trim(),
          descripcion: ptDescripcion.trim(),
          cantidad: Number(ptCantidad) || 1,
        },
        componentes,
      };

      if (isNew) {
        await recetaService.create({ codigoReceta: codigo.trim(), ...payload });
      } else {
        await recetaService.update(codigo.trim(), payload);
      }
      enqueueSnackbar('Receta guardada', { variant: 'success' });

      const codigoPt = ptCodigo.trim();
      if (codigoPt) {
        try {
          const p = await productoService.getProducto(codigoPt, OPTIONS_PRODUCTO_OPERATIVO);
          const costoFicha =
            Number(
              (p as { costoCatalogo?: number }).costoCatalogo
                ?? (p as { descuentos?: { costoCatalogo?: number }[] }).descuentos?.[0]?.costoCatalogo
                ?? p.costo
                ?? 0,
            ) || 0;
          setPtCostoActual(costoFicha);
        } catch {
          // Se mantiene el costo en memoria si no se puede refrescar
        }
        setPendingCostoUnitario(costoUnitarioPt);
        setConfirmCostoOpen(true);
      } else {
        navigate('/produccion/recetas');
      }
    } catch (error: unknown) {
      const msg =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Error al guardar la receta';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmarActualizarCosto = async () => {
    try {
      setUpdatingCosto(true);
      await recetaService.actualizarCostoProductoTerminado(ptCodigo.trim(), pendingCostoUnitario);
      enqueueSnackbar('Costo del producto terminado actualizado', { variant: 'success' });
      finishAndNavigate();
    } catch (error: unknown) {
      const msg =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Error al actualizar el costo del producto';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setUpdatingCosto(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Button startIcon={<ArrowBack />} onClick={() => navigate('/produccion/recetas')} sx={{ textTransform: 'none' }}>
              Recetas
            </Button>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              {isNew ? 'Nueva receta' : 'Editar receta'}
            </Typography>
          </Stack>

          <Stack spacing={2.5}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Código de receta"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value)}
                disabled={!isNew}
                required
                fullWidth
              />
              <TextField
                label="Descripción"
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                required
                fullWidth
              />
            </Stack>

            <Typography variant="h6">Producto terminado</Typography>
            <RecetaProductoLookup
              label="Producto terminado"
              codigo={ptCodigo}
              descripcion={ptDescripcion}
              required
              tiposArticulo="PT"
              dialogTitle="Buscar producto terminado"
              mensajeTipoInvalido="El artículo debe ser de tipo Producto Terminado"
              onCodigoChange={(value) => {
                setPtCodigo(value);
                setPtCostoActual(0);
              }}
              onDescripcionChange={setPtDescripcion}
              onResolved={(item) => {
                setPtCodigo(item.codigoArticulo);
                setPtDescripcion(item.descripcion);
                setPtCostoActual(item.costo);
              }}
            />
            <TextField
              label="Cantidad de producto terminado"
              type="number"
              value={ptCantidad}
              onChange={(e) => setPtCantidad(Number(e.target.value))}
              sx={{ maxWidth: 280 }}
              inputProps={{ min: 0, step: 'any' }}
            />

            <RecetaComponentesGrid
              lineas={lineas}
              onLineaChange={(id, field, value) => {
                setLineas((prev) =>
                  prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
                );
              }}
              onLineaFilled={(id, data) => {
                setLineas((prev) =>
                  prev.map((row) => (row.id === id ? { ...row, ...data } : row)),
                );
              }}
              onEliminarLinea={(id) => {
                setLineas((prev) => (prev.length <= 1 ? prev : prev.filter((row) => row.id !== id)));
              }}
              onAgregarLineaVacia={() => setLineas((prev) => [...prev, newLinea()])}
            />

            <Divider />

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={2}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Costo actual de la receta: {formatCurrency(costoReceta)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Costo unitario del PT (según cantidad {ptCantidad || 0}): {formatCurrency(costoUnitarioPt)}
                </Typography>
              </Box>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
                Guardar
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={confirmCostoOpen} onClose={finishAndNavigate} maxWidth="xs" fullWidth>
        <DialogTitle>Actualizar costo del producto</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body1" sx={{ mb: 1.5 }}>
              ¿Desea actualizar el costo del producto terminado en la ficha del producto?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Producto: {ptCodigo.trim()} — {ptDescripcion.trim() || '—'}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Costo actual (ficha): {formatCurrency(ptCostoActual)}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Nuevo costo (según receta): {formatCurrency(pendingCostoUnitario)}
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={finishAndNavigate} disabled={updatingCosto}>
            No
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmarActualizarCosto}
            disabled={updatingCosto}
          >
            Sí, actualizar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RecetaForm;
